import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, LinearProgress, Chip,
} from '@mui/material';
import { Search as SearchIcon, Add, ArrowForward, Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { eventBus } from '../../services/EventBus';
import {
  androidtvDiscover, androidtvAddByIp, fetchAndroidTvAdminSummary,
  fetchAndroidTvDevices, androidtvPairStart, androidtvPairFinish,
  AndroidTvDevice,
} from '../../services/androidtvApi';
import {
  DiscoveryCell,
  DiscoveryRegisteredTable,
  discoveryRegisteredShellSx,
  DiscoverySortableHeader,
  discoveryRegisteredCol as col,
  discoveryRegisteredRowSx,
  useDiscoveryColumnSort,
} from '../shared/DiscoverySortableHeader';

const CARD_BG = '#1a1a1a';
const BLUE = '#2391FF';

interface DiscoverResultRow {
  ip: string;
  nombre?: string;
  result: 'added' | 'updated_ip' | 'already_known' | 'error';
  detail?: string;
}

type AndroidResultSortKey = 'name' | 'ip' | 'online' | 'result';
type AndroidDeviceSortKey = 'name' | 'type' | 'ip' | 'model' | 'power' | 'status' | 'via';
type AndroidPairSortKey = 'name' | 'ip';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onGoToDevices?: () => void;
}

const AndroidTvDiscoveryTab: React.FC<Props> = ({
  isDemoUser = false, onDemoActionAttempt, onGoToDevices,
}) => {
  const { t, i18n } = useTranslation();
  const [ipInput, setIpInput] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState<{ round: number; total: number } | null>(null);
  const [rows, setRows] = useState<DiscoverResultRow[]>([]);
  const [summary, setSummary] = useState('');
  const [devices, setDevices] = useState<AndroidTvDevice[]>([]);
  const [unassigned, setUnassigned] = useState<number | null>(null);
  const [pairDeviceId, setPairDeviceId] = useState<number | null>(null);
  const [pairPin, setPairPin] = useState('');
  const [pairing, setPairing] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(localStorage.getItem('lastAndroidTvDiscovery'));
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const unpaired = devices.filter((d) => !d.paired);

  const refreshDevices = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        fetchAndroidTvAdminSummary(),
        fetchAndroidTvDevices(),
      ]);
      setUnassigned(s.unassigned_count);
      setDevices(list);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { void refreshDevices(); }, [refreshDevices]);

  const markRun = () => {
    const now = new Date();
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    const formatted = now.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    setLastRun(formatted);
    localStorage.setItem('lastAndroidTvDiscovery', formatted);
  };

  const setCountsSummary = (rowsBuilt: DiscoverResultRow[]) => {
    const added = rowsBuilt.filter((r) => r.result === 'added').length;
    const updated = rowsBuilt.filter((r) => r.result === 'updated_ip').length;
    const known = rowsBuilt.filter((r) => r.result === 'already_known').length;
    setSummary(t('settings.androidtvDiscovery.summary', { added, updated, known }));
  };

  const handleDiscover = async () => {
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    setDiscovering(true);
    setError(null);
    setInfo(null);
    setRows([]);
    setSummary('');
    const rounds = 3;
    setProgress({ round: 1, total: rounds });
    const timer = setInterval(() => {
      setProgress((p) => (p && p.round < p.total ? { ...p, round: p.round + 1 } : p));
    }, 2500);
    try {
      const data = await androidtvDiscover();
      clearInterval(timer);
      setProgress(null);
      const built: DiscoverResultRow[] = [
        ...(data.added || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, result: 'added' as const })),
        ...(data.updated_ip || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, result: 'updated_ip' as const })),
        ...(data.already_known || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, result: 'already_known' as const })),
        ...(data.errors || []).map((e: any) => ({ ip: e.ip || '—', result: 'error' as const, detail: e.error })),
      ];
      setRows(built);
      setCountsSummary(built);
      markRun();
      eventBus.emit('estructura:refrescar');
      void refreshDevices();
    } catch (e: any) {
      clearInterval(timer);
      setProgress(null);
      setError(e?.response?.data?.detail || t('settings.androidtvDiscovery.errorDiscover'));
    } finally {
      setDiscovering(false);
    }
  };

  const handleAddByIp = async () => {
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    const ips = ipInput.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (ips.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      const data = await androidtvAddByIp(ips);
      const built: DiscoverResultRow[] = (data.results || []).map((r: any) => ({
        ip: r.ip,
        nombre: r.device?.nombre,
        result: r.result,
        detail: r.error,
      }));
      setRows(built);
      setCountsSummary(built);
      setIpInput('');
      markRun();
      eventBus.emit('estructura:refrescar');
      void refreshDevices();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.androidtvDiscovery.errorAdd'));
    } finally {
      setAdding(false);
    }
  };

  const startPair = async (id: number) => {
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    setPairing(true);
    setError(null);
    setInfo(null);
    try {
      await androidtvPairStart(id);
      setPairDeviceId(id);
      setPairPin('');
      setInfo(t('settings.androidtvDiscovery.pinHint'));
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.androidtvDiscovery.errorPairStart'));
    } finally {
      setPairing(false);
    }
  };

  const finishPair = async () => {
    if (!pairDeviceId || !pairPin.trim()) return;
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    setPairing(true);
    setError(null);
    try {
      await androidtvPairFinish(pairDeviceId, pairPin.trim());
      setInfo(t('settings.androidtvDiscovery.pairOk'));
      setPairDeviceId(null);
      setPairPin('');
      eventBus.emit('estructura:refrescar');
      void refreshDevices();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.androidtvDiscovery.errorPairFinish'));
    } finally {
      setPairing(false);
    }
  };

  const resultLabel = (r: DiscoverResultRow) => {
    switch (r.result) {
      case 'added': return { text: `✓ ${t('settings.androidtvDiscovery.resultAdded')}`, color: '#66bb6a' };
      case 'updated_ip': return { text: `↻ ${t('settings.androidtvDiscovery.resultUpdated')}`, color: '#e5a54b' };
      case 'already_known': return { text: `— ${t('settings.androidtvDiscovery.resultKnown')}`, color: '#999' };
      default: return {
        text: `✕ ${r.detail || t('settings.androidtvDiscovery.resultError')}`,
        color: '#e57373',
      };
    }
  };

  const getResultSortValue = useCallback((r: DiscoverResultRow, key: AndroidResultSortKey) => {
    if (key === 'name') return r.nombre || r.ip || '';
    if (key === 'ip') return r.ip || '';
    if (key === 'online') return '—';
    return resultLabel(r).text;
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- labels follow t

  const androidStatus = useCallback((d: AndroidTvDevice) => {
    if (!d.paired) return { text: t('settings.discoveryCols.statusUnpaired'), color: '#e5a54b' };
    return d.online
      ? { text: t('settings.discoveryCols.statusOnline'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.statusOffline'), color: '#999' };
  }, [t]);

  const androidPower = useCallback((d: AndroidTvDevice) => {
    if (d.is_on == null) return { text: '—', color: '#888' };
    return d.is_on
      ? { text: t('settings.discoveryCols.powerOn'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.powerOff'), color: '#999' };
  }, [t]);

  const getDeviceSortValue = useCallback((d: AndroidTvDevice, key: AndroidDeviceSortKey) => {
    if (key === 'name') return d.nombre || d.ip || '';
    if (key === 'type') return t('settings.discoveryCols.kindTv');
    if (key === 'ip') return d.ip || '';
    if (key === 'model') return d.modelo || '';
    if (key === 'power') return androidPower(d).text;
    if (key === 'via') return t('settings.discoveryCols.viaLan');
    return androidStatus(d).text;
  }, [t, androidPower, androidStatus]);

  const getPairSortValue = useCallback((d: AndroidTvDevice, key: AndroidPairSortKey) => {
    if (key === 'name') return d.nombre || d.ip || '';
    return d.ip || '';
  }, []);

  const {
    sorted: sortedRows, sortKey: resultSortKey, sortDir: resultSortDir, toggleSort: toggleResultSort,
  } = useDiscoveryColumnSort(rows, getResultSortValue);
  const {
    sorted: sortedDevices, sortKey: deviceSortKey, sortDir: deviceSortDir, toggleSort: toggleDeviceSort,
  } = useDiscoveryColumnSort(devices, getDeviceSortValue);
  const {
    sorted: sortedUnpaired, sortKey: pairSortKey, sortDir: pairSortDir, toggleSort: togglePairSort,
  } = useDiscoveryColumnSort(unpaired, getPairSortValue);

  const deviceRowSx = {
    display: 'flex',
    alignItems: 'center',
    py: 0.45,
    borderBottom: '1px solid #262626',
    minWidth: 0,
  } as const;

  const colNameSx = {
    width: '35%',
    flex: '0 0 35%',
    minWidth: 0,
    fontSize: '0.78rem',
    color: '#fff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pr: 0.5,
  } as const;

  const colIpSx = {
    width: '20%',
    flex: '0 0 20%',
    minWidth: 0,
    fontSize: '0.7rem',
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    pr: 0.5,
  } as const;

  const colOnlineSx = {
    width: '22%',
    flex: '0 0 22%',
    minWidth: 0,
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pr: 0.5,
  } as const;

  const colStatusSx = {
    width: '23%',
    flex: '0 0 23%',
    minWidth: 0,
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2, touchAction: 'pan-y' }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 1.25, lineHeight: 1.4 }}>
        {t('settings.androidtvDiscovery.help')}
      </Typography>

      {/* Descubrimiento en red */}
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {t('settings.androidtvDiscovery.discoverTitle')}
          </Typography>
          {lastRun && (
            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
              {t('settings.androidtvDiscovery.lastRun')}: {lastRun}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon />}
          disabled={discovering}
          onClick={handleDiscover}
          sx={{ backgroundColor: BLUE, textTransform: 'none', '&:hover': { backgroundColor: '#1a7ae0' } }}
        >
          {discovering
            ? t('settings.androidtvDiscovery.discovering')
            : t('settings.androidtvDiscovery.discoverBtn')}
        </Button>
        {progress && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mb: 0.5 }}>
              {t('settings.androidtvDiscovery.round', { round: progress.round, total: progress.total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.round / progress.total) * 100}
              sx={{ height: 6, borderRadius: 3, backgroundColor: '#333', '& .MuiLinearProgress-bar': { backgroundColor: BLUE } }}
            />
          </Box>
        )}
      </Box>

      {/* Alta por IP */}
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.androidtvDiscovery.addByIpTitle')}
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={5}
          placeholder={t('settings.androidtvDiscovery.ipPlaceholder')}
          value={ipInput}
          onChange={(e) => setIpInput(e.target.value)}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': { backgroundColor: '#0f0f0f', color: '#fff' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
          }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          disabled={adding || !ipInput.trim()}
          onClick={handleAddByIp}
          sx={{ backgroundColor: BLUE, textTransform: 'none', '&:hover': { backgroundColor: '#1a7ae0' } }}
        >
          {t('settings.androidtvDiscovery.addBtn')}
        </Button>
      </Box>

      {error && <Typography sx={{ color: '#e57373', fontSize: '0.8rem', mb: 1 }}>{error}</Typography>}
      {info && <Typography sx={{ color: '#81c784', fontSize: '0.8rem', mb: 1 }}>{info}</Typography>}

      {/* Resultado del último descubrimiento */}
      {(rows.length > 0 || summary) && (
        <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
          {summary && <Typography sx={{ fontSize: '0.8rem', color: BLUE, mb: 1 }}>{summary}</Typography>}
          {rows.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 0.5 }}>
                {t('settings.androidtvDiscovery.lastRunNote')}
              </Typography>
              <DiscoverySortableHeader
                columns={[
                  { key: 'name', label: t('settings.discoveryCols.name', { defaultValue: 'Nombre' }), sx: colNameSx },
                  { key: 'ip', label: t('settings.discoveryCols.ip', { defaultValue: 'IP' }), sx: colIpSx },
                  { key: 'online', label: t('settings.discoveryCols.online', { defaultValue: 'En línea' }), sx: colOnlineSx },
                  { key: 'result', label: t('settings.discoveryCols.result', { defaultValue: 'Resultado' }), sx: colStatusSx },
                ]}
                sortKey={resultSortKey}
                sortDir={resultSortDir}
                onToggle={toggleResultSort}
              />
              {sortedRows.map((r, i) => {
                const rl = resultLabel(r);
                return (
                  <Box key={`${r.ip}-${i}`} sx={deviceRowSx}>
                    <Typography sx={colNameSx} title={r.nombre || r.ip}>{r.nombre || r.ip}</Typography>
                    <Typography sx={colIpSx}>{r.ip}</Typography>
                    <Typography sx={{ ...colOnlineSx, color: '#888' }}>—</Typography>
                    <Typography sx={{ ...colStatusSx, color: rl.color }}>{rl.text}</Typography>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      )}

      {/* Dispositivos registrados (DB) */}
      <Box sx={discoveryRegisteredShellSx({ mb: unpaired.length > 0 ? 1.5 : 0 })}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.androidtvDiscovery.devicesTitle')}
        </Typography>
        {devices.length === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
            {t('settings.androidtvDiscovery.emptyDevices')}
          </Typography>
        ) : (
          <>
            <DiscoveryRegisteredTable
              columns={[
                { key: 'name', label: t('settings.discoveryCols.name'), sx: col.name },
                { key: 'type', label: t('settings.discoveryCols.type'), sx: col.type },
                { key: 'ip', label: t('settings.discoveryCols.ip'), sx: col.ip },
                { key: 'model', label: t('settings.discoveryCols.model'), sx: col.model },
                { key: 'power', label: t('settings.discoveryCols.power'), sx: col.power },
                { key: 'status', label: t('settings.discoveryCols.status'), sx: col.status },
                { key: 'via', label: t('settings.discoveryCols.via'), sx: col.via },
              ]}
              sortKey={deviceSortKey}
              sortDir={deviceSortDir}
              onToggle={toggleDeviceSort}
            >
            {sortedDevices.map((d) => {
              const pl = androidPower(d);
              const sl = androidStatus(d);
              return (
                <Box key={d.id} sx={discoveryRegisteredRowSx}>
                  <DiscoveryCell label={t('settings.discoveryCols.name')} sx={col.name} title={d.nombre || d.ip}>{d.nombre || d.ip}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.type')} sx={col.type}>{t('settings.discoveryCols.kindTv')}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.ip')} sx={col.ip}>{d.ip || '—'}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.model')} sx={col.model} title={d.modelo || undefined}>{d.modelo || '—'}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.power')} sx={{ ...col.power, color: pl.color }}>{pl.text}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.status')} sx={{ ...col.status, color: sl.color }}>{sl.text}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.via')} sx={col.via}>{t('settings.discoveryCols.viaLan')}</DiscoveryCell>
                </Box>
              );
            })}
            </DiscoveryRegisteredTable>
          </>
        )}
      </Box>

      {/* Emparejamiento (solo Android TV) */}
      {unpaired.length > 0 && (
        <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, border: '1px solid #333' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            {t('settings.androidtvDiscovery.pairTitle')}
          </Typography>
          <DiscoverySortableHeader
            columns={[
              { key: 'name', label: t('settings.discoveryCols.name', { defaultValue: 'Nombre' }), sx: colNameSx },
              { key: 'ip', label: t('settings.discoveryCols.ip', { defaultValue: 'IP' }), sx: colIpSx },
            ]}
            sortKey={pairSortKey}
            sortDir={pairSortDir}
            onToggle={togglePairSort}
          />
          {sortedUnpaired.map((d) => (
            <Box key={d.id} sx={deviceRowSx}>
              <Typography sx={colNameSx} title={d.nombre}>{d.nombre}</Typography>
              <Typography sx={colIpSx}>{d.ip}</Typography>
              <Button
                size="small"
                startIcon={<LinkIcon />}
                disabled={pairing}
                onClick={() => void startPair(d.id)}
                sx={{ textTransform: 'none', color: BLUE, flexShrink: 0, minWidth: 0, py: 0, ml: 'auto' }}
              >
                {t('settings.androidtvDiscovery.pairBtn')}
              </Button>
            </Box>
          ))}
          {pairDeviceId != null && (
            <Box sx={{ mt: 1.25, display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('settings.androidtvDiscovery.pinPlaceholder')}
                value={pairPin}
                onChange={(e) => setPairPin(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { backgroundColor: '#0f0f0f', color: '#fff' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={pairing || !pairPin.trim()}
                onClick={() => void finishPair()}
                sx={{ backgroundColor: BLUE, textTransform: 'none' }}
              >
                {t('settings.androidtvDiscovery.confirmPin')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {unassigned !== null && unassigned > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            icon={<ArrowForward />}
            label={t('settings.androidtvDiscovery.goToDevices', { count: unassigned })}
            onClick={onGoToDevices}
            clickable={!!onGoToDevices}
            sx={{ backgroundColor: '#262626', color: '#fff', '& .MuiChip-icon': { color: BLUE } }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AndroidTvDiscoveryTab;
